<template>
  <div class="space-y-6">
    <div
      class="bg-white rounded-lg shadow-[4px_3px_0_0_rgba(0,0,0,1)] sm:shadow-[6px_4px_0_0_rgba(0,0,0,1)] border-2 border-black mb-6 sm:mb-8 w-full"
    >
      <div class="bg-blue-50 p-4 sm:p-6 rounded-t-lg">
        <div class="flex items-center mb-1">
          <div class="w-6 h-6 sm:w-8 sm:h-8 rounded-lg flex items-center justify-start">
            <BookOpenIcon class="w-5 h-5 sm:w-6 sm:h-6 text-blue-800" />
          </div>
          <h2 class="text-xl sm:text-2xl font-semibold text-blue-900 ml-2">Tutoring Sessions</h2>
        </div>
        <p class="text-sm sm:text-base text-blue-800 font-semibold">
          Manage your upcoming tutoring sessions.
        </p>
      </div>

      <div class="p-3 sm:p-4 md:p-6">
        <!-- Search and Filter Section -->
        <SessionSearchAndFilter
          v-model:searchQuery="searchQuery"
          v-model:statusFilter="statusFilter"
          v-model:subjectFilter="subjectFilter"
          filter-context="active"
        />

        <!-- Loading State -->
        <div v-if="isLoading" class="flex justify-center items-center py-12">
          <div class="text-center">
            <div
              class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"
            ></div>
            <p class="text-gray-600">Loading sessions...</p>
          </div>
        </div>

        <!-- Upcoming Sessions Section -->
        <UpcomingSessionsSection
          v-if="!isLoading && !hideUpcomingSessions"
          :sessions="paginatedUpcomingSessions"
          :total-sessions="filteredUpcomingSessions.length"
          @reschedule="openRescheduleModal"
          @cancel="openCancelModal"
        />

        <!-- Upcoming Sessions Pagination -->
        <div
          v-if="!isLoading && !hideUpcomingSessions && filteredUpcomingSessions.length > 0"
          class="bg-white rounded-lg mt-4 pb-3 sm:pb-5 px-3 sm:px-6"
        >
          <div
            class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0"
          >
            <div class="text-xs sm:text-sm text-gray-600 order-2 sm:order-1">
              Showing {{ upcomingStartIndex + 1 }}-{{ upcomingEndIndex }} of
              {{ filteredUpcomingSessions.length }} upcoming sessions
            </div>
            <div
              class="flex items-center space-x-1 overflow-x-auto w-full sm:w-auto order-1 sm:order-2"
            >
              <button
                @click="previousUpcomingPage"
                :disabled="upcomingCurrentPage === 1"
                class="border-2 border-blue-800 rounded-md px-2 sm:px-3 py-1 text-xs sm:text-sm text-blue-700 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1 flex-shrink-0"
                title="Previous page"
              >
                <ChevronLeftIcon class="w-3 h-3 sm:w-4 sm:h-4" />
                <span class="hidden sm:inline">Previous</span>
                <span class="sm:hidden">Prev</span>
              </button>
              <template v-for="(page, index) in upcomingDisplayPages" :key="'upcoming-' + index">
                <span
                  v-if="page === '...'"
                  class="px-2 sm:px-3 py-1 text-gray-400 text-xs sm:text-sm"
                >
                  ...
                </span>
                <button
                  v-else
                  @click="goToUpcomingPage(page as number)"
                  :class="[
                    'px-2 sm:px-3 py-1 rounded-md border-2 border-blue-800 transition-colors min-w-[32px] sm:min-w-[40px] text-xs sm:text-sm flex-shrink-0',
                    page === upcomingCurrentPage
                      ? 'bg-blue-600 text-white font-semibold'
                      : 'text-blue-700 hover:bg-blue-50',
                  ]"
                >
                  {{ page }}
                </button>
              </template>
              <button
                @click="nextUpcomingPage"
                :disabled="upcomingCurrentPage === upcomingTotalPages"
                class="border-2 border-blue-800 rounded-md px-2 sm:px-3 py-1 text-xs sm:text-sm text-blue-700 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1 flex-shrink-0"
                title="Next page"
              >
                <span class="hidden sm:inline">Next</span>
                <span class="sm:hidden">Next</span>
                <ChevronRightIcon class="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>
        </div>

        <!-- Completed Sessions Section -->
        <CompletedSessionsSection
          v-if="!isLoading && !hideCompletedSessions"
          :sessions="paginatedCompletedSessions"
          :total-sessions="filteredCompletedSessions.length"
          :hide-interaction-buttons="hideInteractionButtons"
          :show-post-test-button="showPostTestButton"
          :show-pre-test-button="showPreTestButton"
          @take-post-test="handleTakePostTest"
          @take-pre-test="handleTakePreTest"
        />

        <!-- Completed Sessions Pagination -->
        <div
          v-if="!isLoading && !hideCompletedSessions && filteredCompletedSessions.length > 0"
          class="bg-white rounded-lg mt-4 pb-3 sm:pb-5 px-3 sm:px-6"
        >
          <div
            class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0"
          >
            <div class="text-xs sm:text-sm text-gray-600 order-2 sm:order-1">
              Showing {{ completedStartIndex + 1 }}-{{ completedEndIndex }} of
              {{ filteredCompletedSessions.length }} completed sessions
            </div>
            <div
              class="flex items-center space-x-1 overflow-x-auto w-full sm:w-auto order-1 sm:order-2"
            >
              <button
                @click="previousCompletedPage"
                :disabled="completedCurrentPage === 1"
                class="border-2 border-blue-800 rounded-md px-2 sm:px-3 py-1 text-xs sm:text-sm text-blue-700 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1 flex-shrink-0"
                title="Previous page"
              >
                <ChevronLeftIcon class="w-3 h-3 sm:w-4 sm:h-4" />
                <span class="hidden sm:inline">Previous</span>
                <span class="sm:hidden">Prev</span>
              </button>
              <template v-for="(page, index) in completedDisplayPages" :key="'completed-' + index">
                <span
                  v-if="page === '...'"
                  class="px-2 sm:px-3 py-1 text-gray-400 text-xs sm:text-sm"
                >
                  ...
                </span>
                <button
                  v-else
                  @click="goToCompletedPage(page as number)"
                  :class="[
                    'px-2 sm:px-3 py-1 rounded-md border-2 border-blue-800 transition-colors min-w-[32px] sm:min-w-[40px] text-xs sm:text-sm flex-shrink-0',
                    page === completedCurrentPage
                      ? 'bg-blue-600 text-white font-semibold'
                      : 'text-blue-700 hover:bg-blue-50',
                  ]"
                >
                  {{ page }}
                </button>
              </template>
              <button
                @click="nextCompletedPage"
                :disabled="completedCurrentPage === completedTotalPages"
                class="border-2 border-blue-800 rounded-md px-2 sm:px-3 py-1 text-xs sm:text-sm text-blue-700 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1 flex-shrink-0"
                title="Next page"
              >
                <span class="hidden sm:inline">Next</span>
                <span class="sm:hidden">Next</span>
                <ChevronRightIcon class="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Session Modals -->
    <SessionRescheduleModal
      v-if="isRescheduleModalOpen"
      :open="isRescheduleModalOpen"
      :session="selectedSession || undefined"
      @close="closeRescheduleModal"
      @reschedule="handleReschedule"
    />

    <SessionCancelModal
      v-if="isCancelModalOpen"
      :open="isCancelModalOpen"
      :session="selectedSession || undefined"
      @close="closeCancelModal"
      @cancel="handleCancel"
    />

    <!-- Success/Error Notification -->
    <SessionActionNotification
      :show="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="closeNotification"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import UpcomingSessionsSection from "./UpcomingSessions.vue";
import CompletedSessionsSection from "./CompletedSessions.vue";
import SessionSearchAndFilter from "./SessionSearchAndFilter.vue";
import SessionRescheduleModal from "./SessionRescheduleModal.vue";
import SessionCancelModal from "./SessionCancelModal.vue";
import SessionActionNotification from "./SessionActionNotification.vue";
import { BookOpenIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/24/outline";
import type { UpcomingSession, CompletedSession } from "@/types";
import { sessionService } from "@/services/sessionService";
import { useTutorSessionStore } from "@/composables/useTutorSessionStore";
import { auth } from "@/firebase";
import { logError } from "@/utils/secureLogger";

interface Props {
  upcomingSessions?: UpcomingSession[];
  completedSessions?: CompletedSession[];
  totalCompletedSessions?: number;
  hideHeader?: boolean;
  isLoading?: boolean;
  hideUpcomingSessions?: boolean; // New prop to hide upcoming sessions section
  hideCompletedSessions?: boolean; // New prop to hide completed sessions section
  hideInteractionButtons?: boolean; // New prop to hide ratings/feedback/message buttons
  showPostTestButton?: boolean; // New prop to show "Take Post-Test" button instead of "Generate AI"
  showPreTestButton?: boolean; // New prop to show "Take Pre-Test" button instead of "Generate AI"
}

const props = withDefaults(defineProps<Props>(), {
  upcomingSessions: () => [],
  completedSessions: () => [],
  totalCompletedSessions: 0,
  hideHeader: false,
  isLoading: false,
  hideUpcomingSessions: false, // Default: show upcoming sessions
  hideCompletedSessions: false, // Default: show completed sessions
  hideInteractionButtons: false, // Default: show interaction buttons
  showPostTestButton: false, // Default: show Generate AI button
  showPreTestButton: false, // Default: show Generate AI button
});

const emit = defineEmits<{
  takePostTest: [session: CompletedSession];
  takePreTest: [session: CompletedSession];
}>();

const searchQuery = ref("");
const statusFilter = ref("all");
const subjectFilter = ref("all");

// Use tutor session store for calendar event deletion and blocked slots
const { deleteSessionCalendarEvent, removeFromBlockedSlots } = useTutorSessionStore();

// Modal state
const isRescheduleModalOpen = ref(false);
const isCancelModalOpen = ref(false);
const selectedSession = ref<UpcomingSession | null>(null);

// Notification state
const showNotification = ref(false);
const notificationType = ref<"success" | "error">("success");
const notificationTitle = ref("");
const notificationMessage = ref("");

// Filter upcoming sessions based on search and filters
const filteredUpcomingSessions = computed(() => {
  let filtered = props.upcomingSessions;

  // Apply search filter
  if (searchQuery.value) {
    filtered = filtered.filter(
      (session) =>
        session.tutorName.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        session.subject.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
  }

  // Apply status filter
  if (statusFilter.value !== "all") {
    filtered = filtered.filter((session) => session.status === statusFilter.value);
  }

  // Apply subject filter
  if (subjectFilter.value !== "all") {
    filtered = filtered.filter((session) =>
      session.subject.toLowerCase().includes(subjectFilter.value.toLowerCase())
    );
  }

  return filtered;
});

// Filter completed sessions based on search and filters
const filteredCompletedSessions = computed(() => {
  let filtered = props.completedSessions;

  // Apply search filter
  if (searchQuery.value) {
    filtered = filtered.filter(
      (session) =>
        session.tutorName.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
        session.subject.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
  }

  // Apply subject filter
  if (subjectFilter.value !== "all") {
    filtered = filtered.filter((session) =>
      session.subject.toLowerCase().includes(subjectFilter.value.toLowerCase())
    );
  }

  return filtered;
});

// Pagination state for Upcoming Sessions
const upcomingCurrentPage = ref(1);
const upcomingItemsPerPage = 4; // Show 4 items per page (2x2 grid)

const upcomingStartIndex = computed(() => (upcomingCurrentPage.value - 1) * upcomingItemsPerPage);
const upcomingEndIndex = computed(() =>
  Math.min(upcomingStartIndex.value + upcomingItemsPerPage, filteredUpcomingSessions.value.length)
);
const upcomingTotalPages = computed(() =>
  Math.ceil(filteredUpcomingSessions.value.length / upcomingItemsPerPage)
);

// Paginated upcoming sessions
const paginatedUpcomingSessions = computed(() => {
  const start = upcomingStartIndex.value;
  const end = upcomingEndIndex.value;
  return filteredUpcomingSessions.value.slice(start, end);
});

// Pagination state for Completed Sessions
const completedCurrentPage = ref(1);
const completedItemsPerPage = 4; // Show 4 items per page (2x2 grid)

const completedStartIndex = computed(
  () => (completedCurrentPage.value - 1) * completedItemsPerPage
);
const completedEndIndex = computed(() =>
  Math.min(
    completedStartIndex.value + completedItemsPerPage,
    filteredCompletedSessions.value.length
  )
);
const completedTotalPages = computed(() =>
  Math.ceil(filteredCompletedSessions.value.length / completedItemsPerPage)
);

// Paginated completed sessions
const paginatedCompletedSessions = computed(() => {
  const start = completedStartIndex.value;
  const end = completedEndIndex.value;
  return filteredCompletedSessions.value.slice(start, end);
});

// Smart pagination display - shows limited page numbers with ellipsis
const getDisplayPages = (currentPage: number, totalPages: number) => {
  const pages: (number | string)[] = [];

  if (totalPages <= 7) {
    // Show all pages if 7 or fewer
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    // Always show first page
    pages.push(1);

    if (currentPage <= 3) {
      // Near start: 1, 2, 3, 4, ..., last
      pages.push(2, 3, 4, "...", totalPages);
    } else if (currentPage >= totalPages - 2) {
      // Near end: 1, ..., last-3, last-2, last-1, last
      pages.push("...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      // Middle: 1, ..., current-1, current, current+1, ..., last
      pages.push("...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
    }
  }

  return pages;
};

// Display pages for upcoming sessions
const upcomingDisplayPages = computed(() =>
  getDisplayPages(upcomingCurrentPage.value, upcomingTotalPages.value)
);

// Display pages for completed sessions
const completedDisplayPages = computed(() =>
  getDisplayPages(completedCurrentPage.value, completedTotalPages.value)
);

// Modal methods
const openRescheduleModal = (session: UpcomingSession) => {
  selectedSession.value = session;
  isRescheduleModalOpen.value = true;
};

const closeRescheduleModal = () => {
  isRescheduleModalOpen.value = false;
  selectedSession.value = null;
};

const openCancelModal = (session: UpcomingSession) => {
  selectedSession.value = session;
  isCancelModalOpen.value = true;
};

const closeCancelModal = () => {
  isCancelModalOpen.value = false;
  selectedSession.value = null;
};

const handleReschedule = async (data: {
  sessionId: string;
  newDate: string;
  newTime: string;
  reason?: string;
}) => {
  try {
    // Close the modal and show success notification immediately for better UX
    closeRescheduleModal();

    // Show success notification optimistically
    notificationType.value = "success";
    notificationTitle.value = "Session Rescheduled!";
    notificationMessage.value =
      "Your session has been successfully rescheduled. The tutor will be notified of the change.";
    showNotification.value = true;

    // Call the session service to reschedule the session (continues in background)
    await sessionService.rescheduleSession(data.sessionId, data.newDate, data.newTime, data.reason);
  } catch (error) {
    logError(
      "Error rescheduling session",
      error instanceof Error ? error : new Error(String(error))
    );

    // Show error notification
    notificationType.value = "error";
    notificationTitle.value = "Reschedule Failed";
    notificationMessage.value =
      error instanceof Error ? error.message : "Failed to reschedule session. Please try again.";
    showNotification.value = true;
  }
};

const handleCancel = async (data: {
  sessionId: string;
  reason: string;
  cancelledBy: string;
  cancelledAt: Date;
}) => {
  try {
    // Close the modal and show success notification immediately for better UX
    closeCancelModal();

    // Show success notification optimistically
    notificationType.value = "success";
    notificationTitle.value = "Session Cancelled!";
    notificationMessage.value =
      "Your session has been successfully cancelled. The tutor will be notified.";
    showNotification.value = true;

    // Get the session data to check for calendar event ID and tutor ID
    const session = selectedSession.value;
    if (session) {
      // Delete Google Calendar event if it exists
      if ((session as any).calendarEventId && auth.currentUser) {
        try {
          await deleteSessionCalendarEvent(
            data.sessionId,
            (session as any).calendarEventId,
            auth.currentUser.uid
          );
        } catch (calendarError) {
          logError(
            "Failed to delete Google Calendar event",
            calendarError instanceof Error ? calendarError : new Error(String(calendarError))
          );
          // Don't fail the whole cancellation if calendar deletion fails
        }
      }

      // Remove from blocked slots if tutor ID exists
      if ((session as any).tutorId) {
        try {
          await removeFromBlockedSlots((session as any).tutorId, data.sessionId);
        } catch (blockedSlotsError) {
          logError(
            "Failed to remove session from blocked slots",
            blockedSlotsError instanceof Error
              ? blockedSlotsError
              : new Error(String(blockedSlotsError))
          );
          // Don't fail the whole cancellation if blocked slots removal fails
        }
      }
    }

    // Call the session service to cancel the session (continues in background)
    await sessionService.cancelSession(
      data.sessionId,
      data.reason,
      data.cancelledBy,
      data.cancelledAt
    );
  } catch (error) {
    logError("Error canceling session", error instanceof Error ? error : new Error(String(error)));

    // Show error notification
    notificationType.value = "error";
    notificationTitle.value = "Cancellation Failed";
    notificationMessage.value =
      error instanceof Error ? error.message : "Failed to cancel session. Please try again.";
    showNotification.value = true;
  }
};

// Notification methods
const closeNotification = () => {
  showNotification.value = false;
};

// Post-test handler
const handleTakePostTest = (session: CompletedSession) => {
  emit("takePostTest", session);
};

// Pre-test handler
const handleTakePreTest = (session: CompletedSession) => {
  emit("takePreTest", session);
};

// Pagination methods for Upcoming Sessions
const previousUpcomingPage = () => {
  if (upcomingCurrentPage.value > 1) {
    upcomingCurrentPage.value--;
  }
};

const nextUpcomingPage = () => {
  if (upcomingCurrentPage.value < upcomingTotalPages.value) {
    upcomingCurrentPage.value++;
  }
};

const goToUpcomingPage = (page: number) => {
  upcomingCurrentPage.value = page;
};

// Pagination methods for Completed Sessions
const previousCompletedPage = () => {
  if (completedCurrentPage.value > 1) {
    completedCurrentPage.value--;
  }
};

const nextCompletedPage = () => {
  if (completedCurrentPage.value < completedTotalPages.value) {
    completedCurrentPage.value++;
  }
};

const goToCompletedPage = (page: number) => {
  completedCurrentPage.value = page;
};
</script>
